import { htmlStructure, readStartTag } from './html-structure'

/**
 * code-lexer.ts
 *
 * One linear pass over a page that records what every later question needs: where each bracket's
 * partner is, and which stretches are comments. The analyser asks "where does this call, body or
 * condition end?" thousands of times; answering each by walking forward again made a page with many
 * unclosed calls quadratic and could hang every browser that rendered the top bar. Here each character
 * is visited once and the answers are table lookups.
 *
 * Layered like the browser: html-structure.ts decides where code is; JavaScript rules apply only
 * inside scripts and event-handler attributes, CSS rules only inside styles, and markup between them
 * gets plain bracket pairing with no string or comment rules at all.
 */

export type PageLex = {
  /** For an opening `(`, `{` or `[`: the index just past its partner, or -1 when it has none. 0 elsewhere. */
  closeAfter: Int32Array
  /** For a closing `)`: the index of its `(`, or -1. Lets a regex check look back without walking. */
  openerOf: Int32Array
  /** For the first character of a string, template or regex literal in code: the index just past it. 0 elsewhere. */
  literalEnd: Int32Array
  /** Every comment to blank — HTML, JavaScript and CSS — as [from, to). */
  comments: [number, number][]
  /** Executable scripts and event-handler attributes, from the HTML layer, in document order and disjoint. */
  codeRanges: CodeRange[]
}

export type CodeRange = { start: number; end: number; event: string | null }

/** The script or handler attribute holding `index`, or null outside code. */
export function codeRangeAt(lex: PageLex, index: number): CodeRange | null {
  const at = rangeIndexAt(lex.codeRanges, index, (range) => [range.start, range.end])
  return at < 0 ? null : lex.codeRanges[at]
}

/** Position of the range holding `index` in sorted, disjoint `ranges`, or -1. One binary search. */
export function rangeIndexAt<T>(ranges: readonly T[], index: number, bounds: (range: T) => readonly [number, number]): number {
  let low = 0
  let high = ranges.length - 1
  while (low <= high) {
    const mid = (low + high) >> 1
    const [start, end] = bounds(ranges[mid])
    if (index < start) high = mid - 1
    else if (index >= end) low = mid + 1
    else return mid
  }
  return -1
}

/** Keywords after which a `/` begins a regex rather than a division. */
const REGEX_AFTER_KEYWORD = /\b(?:return|typeof|instanceof|in|of|new|delete|void|throw|case|do|else|yield|await)$/

/** Lex a page. Pure and uncached: the analyser lexes each page once and passes the result along. */
export function lexPage(source: string): PageLex {
  const closeAfter = new Int32Array(source.length)
  const openerOf = new Int32Array(source.length).fill(-1)
  const literalEnd = new Int32Array(source.length)
  const comments: [number, number][] = []
  const codeRanges: PageLex['codeRanges'] = []
  const segments: { start: number; end: number; language: Language }[] = []

  for (const range of htmlStructure(source)) {
    if (range.kind === 'comment') comments.push([range.start, range.end])
    else if (range.kind === 'style') segments.push({ start: range.start, end: range.end, language: 'css' })
    else if (range.kind === 'handler' || range.executable) {
      /* A module script has no `<!--` / `-->` line comments; a classic script and a handler attribute do (Annex B). */
      const isModule = range.kind === 'script' && range.module
      segments.push({ start: range.start, end: range.end, language: isModule ? 'js-module' : 'js' })
      codeRanges.push({ start: range.start, end: range.end, event: range.kind === 'handler' ? range.event : null })
    }
  }

  /* Markup between code is paired too, with no string rules — prose apostrophes are not quotes. */
  segments.sort((a, b) => a.start - b.start)
  let cursor = 0
  for (const segment of [...segments]) {
    if (segment.start > cursor) segments.push({ start: cursor, end: segment.start, language: 'markup' })
    cursor = Math.max(cursor, segment.end)
  }
  if (cursor < source.length) segments.push({ start: cursor, end: source.length, language: 'markup' })

  for (const segment of segments) {
    lexSegment(source, segment.start, segment.end, segment.language, { closeAfter, openerOf, literalEnd, comments })
  }
  return { closeAfter, openerOf, literalEnd, comments, codeRanges }
}

type Language = 'js' | 'js-module' | 'css' | 'markup'

const OPENERS = '({['
const CLOSERS = ')}]'

function lexSegment(
  source: string,
  from: number,
  to: number,
  language: Language,
  out: Omit<PageLex, 'codeRanges'>
): void {
  const { closeAfter, openerOf, literalEnd, comments } = out
  /* One stack per bracket kind, so a stray `]` cannot break the pairing of parentheses. Nothing pairs across a segment edge. */
  const stacks: number[][] = [[], [], []]
  /* After a `/` that did not close as a regex, the rest of its line holds no regex end either: skip re-trying it. */
  let noRegexUntil = -1
  let index = from

  while (index < to) {
    const char = source[index]

    if (language !== 'markup') {
      const js = language === 'js' || language === 'js-module'
      if (char === '"' || char === "'" || (js && char === '`')) {
        literalEnd[index] = endOfString(source, index, char, to)
        index = literalEnd[index]
        continue
      }
      if (source.startsWith('/*', index)) {
        const close = source.indexOf('*/', index + 2)
        const stop = close < 0 || close + 2 > to ? to : close + 2
        comments.push([index, stop])
        index = stop
        continue
      }
      /* `<!--` anywhere, and `-->` first on a line, are line comments in a classic script (ECMAScript Annex B). */
      const annexB = language === 'js' && (source.startsWith('<!--', index) || startsLineWithCloser(source, index, from))
      if (js && (source.startsWith('//', index) || annexB)) {
        const newline = source.indexOf('\n', index)
        const stop = newline < 0 || newline > to ? to : newline
        comments.push([index, stop])
        index = stop
        continue
      }
      if (js && char === '/' && index > noRegexUntil && regexMayStartAt(source, index, from, openerOf)) {
        const end = endOfRegex(source, index, to)
        if (end > index + 1) {
          literalEnd[index] = end
          index = end
          continue
        }
        const newline = source.indexOf('\n', index)
        noRegexUntil = newline < 0 || newline > to ? to : newline
      }
    }

    const opening = OPENERS.indexOf(char)
    if (opening >= 0) {
      stacks[opening].push(index)
      closeAfter[index] = -1
    } else {
      const closing = CLOSERS.indexOf(char)
      if (closing >= 0) {
        const opener = stacks[closing].pop()
        if (opener !== undefined) {
          closeAfter[opener] = index + 1
          if (char === ')') openerOf[index] = opener
        }
      }
    }
    index++
  }
}

/**
 * Whether a `/` at `index` opens a regex literal: it follows an operator, an opening bracket, a comma,
 * a semicolon, a block end, the `)` of an if/while/for/with condition, or a keyword such as `return`.
 * After an identifier, number, other `)` or `]` it is a division.
 */
function regexMayStartAt(source: string, index: number, from: number, openerOf: Int32Array): boolean {
  let before = index - 1
  while (before >= from && /\s/.test(source[before])) before--
  if (before < from) return true
  if (/[(,=:[!&|?{};+\-*%<>~^}]/.test(source[before])) return true
  if (source[before] === ')') {
    const opener = openerOf[before]
    return opener >= 0 && /\b(?:if|while|for|with)\s*$/.test(source.slice(Math.max(from, opener - 8), opener))
  }
  return REGEX_AFTER_KEYWORD.test(source.slice(Math.max(from, before - 10), before + 1))
}

/** Index just past a regex literal starting at `index`, honouring escapes and character classes. `index + 1` when a newline or `limit` comes first. */
function endOfRegex(source: string, index: number, limit: number): number {
  let inClass = false
  for (let at = index + 1; at < limit; at++) {
    const char = source[at]
    if (char === '\n') return index + 1
    if (char === '\\') at++
    else if (char === '[') inClass = true
    else if (char === ']') inClass = false
    else if (char === '/' && !inClass) return at + 1
  }
  return index + 1
}

/** Index just past the closing quote, honouring backslash escapes. An unterminated string runs to `limit`. */
function endOfString(source: string, start: number, quote: string, limit: number): number {
  for (let index = start + 1; index < limit; index++) {
    if (source[index] === '\\') {
      index++
      continue
    }
    if (source[index] === quote) return index + 1
  }
  return limit
}

/** Whether a `-->` at `index` is the first thing on its line within the segment — the only place Annex B reads it as a comment. */
function startsLineWithCloser(source: string, index: number, segmentStart: number): boolean {
  if (!source.startsWith('-->', index)) return false
  const lineStart = Math.max(source.lastIndexOf('\n', index - 1) + 1, segmentStart)
  return /^\s*$/.test(source.slice(lineStart, index))
}

export type StartTag = {
  name: string
  /** The tag as written, from `<` to its `>`. */
  text: string
  index: number
  /** Decoded, as the browser reads them. */
  attributes: Map<string, string>
  /** The tag rebuilt from its decoded attributes, `<name a="v">`: what patterns over a tag should read. */
  decoded: string
}

/**
 * Every start tag in `source` whose name passes `wanted`, in markup and in script strings alike —
 * pages build forms in innerHTML as often as in markup. Two parts, both linear: find `<name` with a
 * plain scan, then read the tag with the browser's own rules (html-structure readStartTag), so a `>`
 * inside a quoted value does not end it. The search resumes after the tag, so tags never overlap: a run
 * of `<x` with no `>` costs one pass, not one pass per `<`.
 */
export function startTags(source: string, wanted: (name: string) => boolean): StartTag[] {
  const tags: StartTag[] = []

  for (let lt = source.indexOf('<'); lt >= 0; lt = source.indexOf('<', lt + 1)) {
    let nameEnd = lt + 1
    if (!/[A-Za-z]/.test(source[nameEnd] ?? '')) continue
    while (nameEnd < source.length && /[\w-]/.test(source[nameEnd])) nameEnd++
    const name = source.slice(lt + 1, nameEnd).toLowerCase()
    if (!wanted(name)) {
      lt = nameEnd - 1
      continue
    }

    const tag = readStartTag(source, lt + 1)
    if (!tag.closed) break
    const decoded = `<${name}${[...tag.attributes].map(([key, value]) => ` ${key}="${value}"`).join('')}>`
    tags.push({ name, text: source.slice(lt, tag.end), index: lt, attributes: tag.attributes, decoded })
    lt = tag.end - 1
  }

  return tags
}
